import { useId } from 'react';

/* Ce que le club rend, rapporté à ce qu'il promet.
 *
 * ── La barre EST la distance annoncée ───────────────────────────────────────
 *
 * Toute sa largeur vaut cent pour cent du chiffre inscrit sur le club. Ce que
 * le joueur atteint se dessine dessus : la fourchette de ses balles en teinte
 * moyenne, sa moyenne en pastille pleine. On lit alors d'un coup la seule
 * chose qui compte pour se fier à un club — « j'en sors quatre-vingt-neuf pour
 * cent » — là où deux nombres côte à côte, 124 et 140, demandaient une
 * division mentale.
 *
 * Une échelle absolue montrait la même chose et ne répondait pas à cette
 * question : elle plaçait deux points sur une règle sans dire lequel était la
 * promesse.
 *
 * ── Quand la barre déborde ──────────────────────────────────────────────────
 *
 * Une balle peut dépasser l'annonce. L'échelle s'étend alors au-delà des cent
 * pour cent, et le repère du club devient un trait DANS la barre plutôt qu'à
 * son bout : la promesse reste visible à sa place, et le dépassement se voit
 * pour ce qu'il est.
 *
 * ── Sans distance annoncée ──────────────────────────────────────────────────
 *
 * Il n'y a plus de cent pour cent, donc plus de pourcentage : la barre repart
 * de zéro et ne porte que les mesures. Inventer une référence reviendrait à
 * juger le joueur sur un chiffre qu'il n'a pas donné.
 */
export function BandeDistances({ min, max, moyenne, annonce, unite }: {
    min: number;
    max: number;
    moyenne: number;
    /** La distance inscrite sur le club, s'il y en a une. C'est le 100 %. */
    annonce?: number | null;
    unite: string;
}) {
    const id = useId();
    /* L'échelle : 0 → l'annonce. Elle ne s'élargit QUE si les balles la
     *  dépassent, et juste assez pour que le trait des cent pour cent reste
     *  dans la barre au lieu d'en être le bord. */
    const plafond = Math.max(annonce ?? max, max) * 1.06;
    const part = (v: number): number => (plafond > 0 ? (v / plafond) * 100 : 0);
    const pourcent = annonce != null && annonce > 0
        ? Math.round((moyenne / annonce) * 100)
        : null;

    return (
        <figure className="m-0" aria-labelledby={`${id}-t`}>
            {/* « 124 m » tout seul ne disait pas 124 m DE QUOI : la plus
                longue, la dernière, la moyenne ? C'est la moyenne, et le mot
                doit suivre le nombre. Le pourcentage nomme lui aussi sa
                référence : « de l'annonce » supposait qu'on sache de quelle
                annonce il s'agit. */}
            <p id={`${id}-t`} className="mb-1 text-center text-xs">
                <span className="font-score text-lg leading-none tabular-nums">{moyenne} {unite}</span>
                <span className="ml-1.5 opacity-60">
                    en moyenne
                    {pourcent != null && (
                        <>
                            ,{' '}
                            <span className="font-score tabular-nums">{pourcent}&nbsp;%</span>
                            {' de la distance théorique'}
                        </>
                    )}
                </span>
            </p>

            <div className="relative h-6 w-full overflow-hidden rounded-full"
                style={{ background: 'color-mix(in oklab, var(--tuile-encre) 10%, transparent)' }}>
                {/* La fourchette atteinte. */}
                <div className="absolute inset-y-0 rounded-full"
                    style={{
                        left: `${part(min)}%`,
                        width: `${Math.max(2, part(max) - part(min))}%`,
                        background: 'color-mix(in oklab, var(--tuile-encre) 42%, transparent)',
                    }} />
                {/* La bulle de la moyenne : pleine, c'est le point d'appui. */}
                <div className="absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full
                                ring-2 ring-[var(--tuile)]"
                    style={{ left: `${part(moyenne)}%`, background: 'var(--tuile-encre)' }} />
                {/* Le trait des cent pour cent, TOUJOURS dessiné. Laissé
                    implicite au bord de la barre, on ne savait plus si le bout
                    était la promesse ou la plus longue balle — et la question
                    ne se pose que dans le cas où la réponse compte, celui du
                    dépassement. */}
                {annonce != null && (
                    <div className="absolute inset-y-0 w-0.5"
                        style={{ left: `${part(annonce)}%`, background: 'var(--brass-ink)' }} />
                )}
            </div>

            {/* Les bouts de la fourchette à gauche, la promesse à droite. */}
            <div className="mt-1 flex items-baseline justify-between text-[10px]">
                <span className="opacity-55">
                    <span className="font-score tabular-nums">{min}</span>
                    {' à '}
                    <span className="font-score tabular-nums">{max} {unite}</span>
                    {' atteints'}
                </span>
                {/* Le même mot que la légende du dessus : « annoncé » ici et
                    « théorique » là-haut auraient désigné la même valeur de deux
                    façons, sur la même figure. */}
                {annonce != null && (
                    <span className="font-bold" style={{ color: 'var(--brass-ink)' }}>
                        <span className="font-score tabular-nums">{annonce} {unite}</span> en théorie
                    </span>
                )}
            </div>
        </figure>
    );
}
